/**
 * 명함 관리 (2026-09-05) — 「환경설정 > 기본환경 > 명함 첨부」의 「명함 관리」가 연다.
 *
 * 정본 P2-1 의 **「항목 추가」로 칸이 느는 폼**:
 *   · 처음에는 거의 다 쓰는 칸만 선다.
 *   · 「항목 추가」는 **남은 것**을 목록으로 보이고, 고르면 그 칸이 폼 **끝에** 붙는다.
 *   · 이미 더한 칸은 목록에서 사라진다 — 남아 있으면 두 번 더해지거나 「눌러도 아무 일이
 *     없는 항목」이 된다.
 *   · 더한 칸은 줄 끝의 「빼기」로 뺀다.
 *
 * ★ 확인을 눌러도 **아직 저장이 아니다.** 값은 환경설정의 초안에 담기고, 저장바의 「저장」이
 *   서버로 보낸다 — 한 화면은 한 가지 반영 시점만 갖는다(정본 P2-1).
 * ★ 사진은 **프로필 사진**을 쓴다. 명함 전용 사진 올리기는 아직 없어서, 여기서는 바꿀 수
 *   없다고 적는다(정본 P3 — 할 수 없는 것은 감추지 말고 왜 못 하는지 적는다).
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { Plus, X } from '../../icons';
import { Avatar } from '../../components/shared/Avatar';
import { Button } from '../../components/shared/Button';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { useAuthStore } from '../../store/authStore';
import { tMail } from '../../lib/mail-i18n';
import type { MailVcard } from '../../api/users';
import { MAIL_VCARD_BASE, MAIL_VCARD_EXTRA, compactVcard, type MailVcardField } from './mailSettings';

interface Props {
  value: MailVcard | null;
  onClose: () => void;
  onDone: (next: MailVcard | null) => void;
}

export function MailVcardModal({ value, onClose, onDone }: Props) {
  const me = useAuthStore((s) => s.user);
  const [form, setForm] = useState<MailVcard>(() => value ?? {});
  /** 더해 세운 칸 — 값이 이미 있는 것은 처음부터 서 있다(고쳐 둔 것이 안 보이면 안 된다). */
  const [added, setAdded] = useState<MailVcardField['id'][]>(
    () => MAIL_VCARD_EXTRA.filter((f) => (value?.[f.id] ?? '') !== '').map((f) => f.id),
  );
  const [pickOpen, setPickOpen] = useState(false);
  const addBtn = useRef<HTMLButtonElement>(null);

  const rest = useMemo(() => MAIL_VCARD_EXTRA.filter((f) => !added.includes(f.id)), [added]);

  const put = useCallback((k: MailVcardField['id'], v: string) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  }, []);

  const addField = useCallback((k: MailVcardField['id']) => {
    setAdded((prev) => (prev.includes(k) ? prev : [...prev, k]));
    setPickOpen(false);
    addBtn.current?.focus();
  }, []);

  const dropField = useCallback((k: MailVcardField['id']) => {
    setAdded((prev) => prev.filter((x) => x !== k));
    // 뺀 칸의 값도 함께 비운다 — 안 그러면 안 보이는 값이 저장된다.
    setForm((prev) => ({ ...prev, [k]: '' }));
  }, []);

  const rows: MailVcardField[] = [
    ...MAIL_VCARD_BASE,
    ...added.map((k) => MAIL_VCARD_EXTRA.find((f) => f.id === k)!).filter(Boolean),
  ];

  return (
    <Modal
      title={tMail('mail.vcard.title')}
      size="sm"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose}>{tMail('mail.vcard.cancel')}</Button>
          <Button variant="primary" onClick={() => onDone(compactVcard(form))}>
            {tMail('mail.vcard.done')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="mailvc">
        <div className="mailvc__photo">
          <Avatar userId={me?.id ?? 0} name={me?.name ?? ''} src={me?.avatarUrl ?? undefined} size="lg" />
          <div>
            <p className="mailvc__photot">{tMail('mail.vcard.photo')}</p>
            <p className="wc-set__hint">{tMail('mail.vcard.photo_hint')}</p>
          </div>
        </div>

        <div className="mailvc__grid">
          {rows.map((f) => {
            const isExtra = MAIL_VCARD_EXTRA.some((x) => x.id === f.id);
            return (
              <label
                key={f.id}
                className={`mailvc__field${f.wide ? ' mailvc__field--wide' : ''}`}
              >
                <span className="mailvc__label">
                  {tMail(f.labelKey)}
                  {isExtra && (
                    <button
                      type="button"
                      className="mailvc__drop"
                      onClick={() => dropField(f.id)}
                      aria-label={`${tMail(f.labelKey)} ${tMail('mail.vcard.remove')}`}
                    >
                      <X size={12} aria-hidden />
                    </button>
                  )}
                </span>
                <Input
                  value={(form[f.id] as string | undefined) ?? ''}
                  onChange={(e) => put(f.id, e.target.value)}
                  maxLength={f.id === 'memo' ? 500 : 300}
                  fullWidth
                />
              </label>
            );
          })}
        </div>

        {/* 항목 추가 — 모달 안에서 또 모달을 열지 않는다. 남은 것만 보이는 작은 목록이다. */}
        <div className="mailvc__add">
          <Button
            ref={addBtn}
            variant="secondary"
            size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            disabled={rest.length === 0}
            aria-expanded={pickOpen}
            aria-haspopup="menu"
            title={rest.length === 0 ? tMail('mail.vcard.all_added') : undefined}
            onClick={() => setPickOpen((v) => !v)}
          >
            {tMail('mail.vcard.add')}
          </Button>
          {pickOpen && rest.length > 0 && (
            <div className="mailvc__menu" role="menu" aria-label={tMail('mail.vcard.add_aria')}>
              {rest.map((f) => (
                <button key={f.id} type="button" role="menuitem" onClick={() => addField(f.id)}>
                  {tMail(f.labelKey)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
